import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { SEO } from '../../components-new/SEO/SEO';
import { AnimatedSection } from '../../components-new/AnimatedSection/AnimatedSection';
import './style.less';

export const About = () => {
  const { t } = useTranslation();

  const stats = [
    { number: '100+', label: t('about.stats.projects', 'Проекта') },
    { number: '8+', label: t('about.stats.years', 'Години опит') },
    { number: '100%', label: t('about.stats.attention', 'Внимание към детайла') }
  ];

  const scrollToContent = () => {
    const heroElement = document.querySelector('.page-hero');
    if (heroElement) {
      const heroBottom = heroElement.getBoundingClientRect().bottom + window.scrollY;
      window.scrollTo({ top: heroBottom, behavior: 'smooth' });
    }
  };

  return (
    <main className="about-page">
      <SEO
        title={t('about.seo.title')}
        description={t('about.seo.description')}
        keywords={t('about.seo.keywords')}
      />

      {/* Hero Section */}
      <section
        className="section page-hero"
        aria-label={t('about.heroLabel', 'Заглавна секция')}
        style={{ backgroundImage: `url('/images/about-me/office.jpg')` }}
      >
        <div className="hero-overlay" aria-hidden="true"></div>
        <div className="container">
          <AnimatedSection className="hero-content">
            <span className="hero-subtitle">{t('about.heroSubtitle', 'Добре дошли в')}</span>
            <h1>{t('about.title')}</h1>
            <div className="hero-line" aria-hidden="true"></div>
          </AnimatedSection>
        </div>
        <button
          className="hero-scroll-indicator"
          onClick={scrollToContent}
          aria-label={t('about.scrollToContent', 'Превъртете надолу към съдържанието')}
          tabIndex={0}
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            aria-hidden="true"
          >
            <i className="fas fa-chevron-down" aria-hidden="true"></i>
          </motion.div>
        </button>
      </section>

      {/* Bio Section - Asymmetric Layout */}
      <section
        className="section bio-section"
        aria-label={t('about.bioSectionLabel', 'За мен')}
      >
        <div className="container">
          <div className="bio-content">
            <AnimatedSection className="bio-image-wrapper">
              <div className="bio-image-container">
                <div className="decorative-frame" aria-hidden="true"></div>
                <img
                  src="/images/about-me/office-desk.jpg"
                  alt={t(
                    'about.profileAlt',
                    'Портрет на Силвия Генчева, интериорен дизайнер и основател на SG Interior Design'
                  )}
                  loading="lazy"
                />
                <div className="decorative-accent" aria-hidden="true"></div>
              </div>
            </AnimatedSection>

            <div className="bio-text-wrapper">
              <AnimatedSection delay={0.1}>
                <span className="section-label">{t('about.bioLabel', 'За мен')}</span>
              </AnimatedSection>
              <AnimatedSection delay={0.2}>
                <h2 className="bio-title">
                  {t('about.bioTitle', 'Създавам пространства, които разказват истории')}
                </h2>
              </AnimatedSection>
              <AnimatedSection delay={0.3}>
                <p className="bio-text">{t('about.text')}</p>
              </AnimatedSection>
              <AnimatedSection delay={0.4}>
                <div className="bio-signature">
                  <span className="signature-name">
                    {t('about.signature', 'Силвия Генчева')}
                  </span>
                  <span className="signature-title">
                    {t('about.signatureTitle', 'Основател & Интериорен Дизайнер')}
                  </span>
                </div>
              </AnimatedSection>
              <AnimatedSection delay={0.5}>
                <div className="bio-stats" role="list">
                  {stats.map((stat, index) => (
                    <div key={index} className="bio-stat-item" role="listitem">
                      <span className="bio-stat-number">{stat.number}</span>
                      <span className="bio-stat-label">{stat.label}</span>
                    </div>
                  ))}
                </div>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section
        className="section philosophy-section"
        aria-label={t('about.philosophyLabel', 'Философия')}
      >
        <div className="container">
          <AnimatedSection className="philosophy-content">
            <div className="quote-icon" aria-hidden="true">
              <i className="fas fa-quote-left" aria-hidden="true"></i>
            </div>
            <blockquote className="philosophy-quote">
              <p>
                {t(
                  'about.philosophy',
                  'Вярвам, че всеки дом трябва да отразява душата на своите обитатели. Моята цел е да създавам не просто красиви пространства, а места, където се чувствате истински у дома.'
                )}
              </p>
            </blockquote>
          </AnimatedSection>
        </div>
      </section>

      {/* Values/Features Section */}
      <section
        className="section features-section"
        aria-label={t('about.valuesLabel', 'Ценности')}
      >
        <div className="container">
          <AnimatedSection className="section-header text-center">
            <span className="section-label">{t('about.valuesLabel', 'Ценности')}</span>
            <h2>{t('about.valuesTitle', 'Какво ме движи')}</h2>
          </AnimatedSection>

          <div className="features-grid">
            <AnimatedSection className="feature-card">
              <div className="feature-icon-wrapper">
                <div className="feature-icon">
                  <i className="fas fa-bullseye" aria-hidden="true"></i>
                </div>
              </div>
              <h3>{t('about.mission.title')}</h3>
              <p>{t('about.mission.text')}</p>
            </AnimatedSection>

            <AnimatedSection className="feature-card" delay={0.15}>
              <div className="feature-icon-wrapper">
                <div className="feature-icon">
                  <i className="fas fa-compass" aria-hidden="true"></i>
                </div>
              </div>
              <h3>{t('about.approach.title')}</h3>
              <p>{t('about.approach.text')}</p>
            </AnimatedSection>

            <AnimatedSection className="feature-card" delay={0.3}>
              <div className="feature-icon-wrapper">
                <div className="feature-icon">
                  <i className="fas fa-heart" aria-hidden="true"></i>
                </div>
              </div>
              <h3>{t('about.passion.title', 'Страст към детайла')}</h3>
              <p>
                {t(
                  'about.passion.text',
                  'Всеки детайл има значение. От избора на материали до последния декоративен елемент - всичко е обмислено до съвършенство.'
                )}
              </p>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </main>
  );
};
